import { FilterGroup } from 'src/app/modules/core/store/models/filters.model';
import { Document } from 'src/app/modules/core/store/models/document.model';
import { TableFilter, WorkflowHost, WorkflowHostType } from 'src/app/modules/core/store/models/workflow.model';
import { TextValueByCulture } from 'src/app/modules/core/store/models/translations.model';
import { ClosestAssetFilterDto } from 'src/app/modules/cop/store/impacted-assets';
import { DocumentQueryParams } from '@app/modules/core/store/models/query.model';

export interface MassNotificationTemplate {
  uId: string;
  createdOn?: any;
  updatedOn?: any;
  name?: string;
  description?: string;
  lastEditedByName?: string;
  lastEditedById?: string;
  type?: MassNotificationType;
  isActive?: boolean;
  massNotificationWorkflowUId: string;
  massNotificationRecipientsWorkflowUId: string;
  templateGroupUId?: string;
  templateGroupName?: string;
  channelsCascadeSettings?: ChannelsCascadeSettings;
  allowedOverrides?: AllowedTemplateOverrides;
  defaultMessageSettings?: MessageSettings;
  recipientSettings?: ToRecipientSettings;
  settings?: string;
  communicationTemplates: CommunicationTemplate[];
  businessUnitUIds: string[];
}

export interface MassNotificationTemplateGroup {
  uId: string;
  createdOn?: any;
  updatedOn?: any;
  name?: string;
  lastEditedByName?: string;
  lastEditedById?: string;
  referenceCount?: number;
}

export interface ToRecipientSettings {
  recipients?: MassNotificationSelectedRecipient[];
  datasetRecipients?: DatasetSelectedRecipientFilter[];
  notificationDocumentSettings?: NotificationDocumentRecipientSettings;
  relatedAssetSettings?: RelatedAssetRecipientSettings;
  allowOverride?: boolean;
  sendToClosestAssets?: boolean;
  sendToClosestAssetsSettings?: SendToClosestAssetSettings[];
}

export interface NotificationDocumentRecipientSettings {
  documentUIds?: string[];
}

export interface RelatedAssetRecipientSettings {
  sendToRelatedAssets?: boolean;
  relatedAssetRecipients?: DatasetRelatedAssetSelectedRecipient[];
}

export interface SendToClosestAssetSettings {
  datasetUId: string;
  layerUId?: string;
  selectedDocumentsInfo?: DocumentSelectionInfo;
  sendToRelatedAssets: boolean; // include related assets based on 'Reference Criteria' filters'
  sendToRefRelatedAssets?: RefRelatedSettings[];
  useSubscribers: boolean; // evaluate against subscriber filters based on email address
  requiresSubscribers?: boolean; // subscribers are required
  withinMeters?: number;
  filter?: FilterGroup;
  exclude: boolean; // exclude this asset
  advancedAssetDistanceSettings?: AdvancedAssetDistanceSettings[];
}

export interface RefRelatedSettings {
  fieldName: string;
  selectedDocumentsInfo?: DocumentSelectionInfo;
}

export interface AdvancedAssetDistanceSettings {
  uId: string;
  filterGroup?: FilterGroup;
  withinMeters: number;
}

export interface DatasetSelectedRecipientFilter {
  uId: string;
  datasetUId: string;
  datasetName: string;
  datasetIcon?: string;
  documentCount?: number;
  sendToRelatedAssets?: boolean;
  sendToRefRelatedAssets?: RefRelatedSettings[];
  filter?: TableFilter;
  selectedUIds?: string[];
  relatedCount?: number;
  relatedContactCount?: number;
  relatedCountDisplay?: string;
  relatedDatasetUId?: string;
  limitedCounts?: any;
  relatedRecipients?: DatasetRelatedAssetSelectedRecipient[];
}

export interface DatasetRelatedAssetSelectedRecipient {
  uId: string;
  refFieldUId: string;
  refFieldName: string;
  datasetUId: string;
  datasetName: string;
  dataasetIcon?: string;
  isCustomIcon: boolean;
  color: string;
  hasDocumentFilter: boolean;
  selectionInfo?: DocumentSelectionInfo;
  include?: boolean;
  limitedCounts?: any;
  documentCount?: DatasetRelatedAssetRecipientCount;
}

export interface DatasetRelatedAssetRecipientCount {
  total: number;
  contactCount: number;
  countDisplay?: string;
}

export interface LayerAssetSummary {
  layerUId: string;
  name: string;
  icon?: string;
  documentCount?: number;
}

export interface ChannelCascadeGroup {
  uId: string;
  startSendType: StartSendType;
  startSendTime?: TimeOffsetOrValue;
  resendType: ResendType;
  resendTime?: TimeOffsetOrValue;
  configuredChannelUIds?: string[];
}

export interface ChannelNotificationSettings {
  uId: string;
  configuredChannelUId?: string;
  startTimeMinutes?: number;
  resendTimeMinutes?: number;
  stopTimeMinutes?: number;
}

export interface ChannelsCascadeSettings {
  advancedMode: boolean;
  channelNotifications?: ChannelNotificationSettings[];
}

export interface MessageSettings {
  notificationMessage?: TextValueByCulture;
  smsNotificationMessage?: TextValueByCulture;
  confirmationText?: TextValueByCulture;
  confirmationButtons?: ConfirmationButton[];
  settings?: string;
}

export interface ConfirmationButton {
  uId: string;
  number: number;
  text?: TextValueByCulture;
  type: ConfirmationType;
  destinationUId?: string;
}

export enum ConfirmationType {
  Negative = 0,
  Positive = 1
}

export interface ChannelCascadeGroup {
  uId: string;
  startSendType: StartSendType;
  startSendTime?: TimeOffsetOrValue;
  resendType: ResendType;
  resendTime?: TimeOffsetOrValue;
  configuredChannelUIds?: string[];
}

export interface SkippedGroupChannels {
  groupUId: string;
  skippedChannelUIs?: string[]; // null means skip the whole group
}

export enum StartSendType {
  None = 0,
  Immediate = 1,
  AfterTime = 2,
  AtSpecificTime = 3
}

export enum ResendType {
  None = 0,
  OneTime = 1,
  WhileNotResponded = 2
}

export interface TimeOffsetOrValue {
  offsetType?: TimeOffsetType;
  offsetValue?: number;
  specificTime?: string;
}

export enum AllowedTemplateOverrides {
  None = 0,
  Channels = 1,
  Cascade = 2,
  SendTime = 4,
  ResendTime = 8,
  Expiration = 16,
  NotificationMessage = 32,
  Recipients = 64
}

export enum TimeOffsetType {
  None = 0,
  Hours = 1,
  Days = 2,
  Weeks = 3,
  Months = 4,
  Years = 5,
  Minutes = 6
}

export interface MassNotificationConfirmationDetails {
  ommunicationRecipientUId?: string;
  communicationUId?: string;
  configuredChannelUId?: string;
  confirmationText?: string;
  confirmationButtons?: ConfirmationButtonItem[];
  culture?: string;
}

export interface ConfirmationButtonItem {
  uId?: string;
  text?: string;
  type: ConfirmationType;
}

export interface CommunicationTemplate {
  uId: string;
  name: string;
  description: string;
  isActive?: boolean;
  createdOn?: any;
  updatedOn?: any;
  lastEditedByName?: string;
  lastEditedById?: string;
  channelType: ChannelType;
  channelUId?: string;
  datasetUId?: string;
  settings?: {
    emailSubject?: TextValueByCulture;
    summary?: TextValueByCulture;
  };
  massNotificationDefaultSettings?: MessageSettings;
  viewControls?: CommunicationTemplateViewControl[];
}

export interface CommunicationTemplateViewControl {
  uId: string;
  controlType: TemplateViewControlType;
  sortOrder: number;
  childControls?: CommunicationTemplateViewControl[];
  settings?: any;
  communicationTemplateUId?: string;
}

export enum MassNotificationItemState {
  None = 0,
  Pending = 1,
  Ready = 2
}

export enum MassNotificationItemSource {
  None = 0,
  Manual = 1,
  WorkflowRule = 2
}

export interface MassNotification extends WorkflowHost {
  uId: string;
  name: string;
  workflowUId: string;
  workflowStepUId: string;
  createdOn: any;
  updatedOn: any;
  lastEditedByName: string;
  lastEditedById: string;
  notificationType: MassNotificationType;
  state: MassNotificationItemState;
  source: MassNotificationItemSource;
  notificationSeverity: MassNotificationSeverity;
  notificationTemplateUId: string;
  channelsCascadeSettings?: ChannelsCascadeSettings;
  viewControlOverrides?: CommunicationTemplateViewControlOverride[];
  messageSettings?: MessageSettings;
  datasetUId?: string;
  documentUId?: string;
  mapUId?: string;
  workflowState: any;
  surveyUId?: string;
  businessUnitUId?: string;
  businessUnitName?: string;
  recipientSettings?: ToRecipientSettings;
  toClosestAssetsSelectionFilter?: ClosestAssetFilterDto;
}

export enum MassNotificationSeverity {
  None = 0,
  Low = 1,
  Moderate = 2,
  High = 3,
  Extreme = 4
}

export interface CommunicationTemplateViewControlOverride {
  templateUId?: string;
  controlUId?: string;
  deleted?: boolean;
  settings?: any;
}

export interface MassNotificationSelectedRecipient {
  uId: string;
  parentUId?: string;
  type: MassRecipientType;
  displayName?: string;
}

export enum MassRecipientType {
  None = 0,
  User = 1,
  Group = 2,
  DistributionList = 3,
  Document = 4,
  Custom = 5,
  ClosestAssets = 6,
  ClosestAssetsRelated = 7
}

export interface MassNotificationRecipient extends WorkflowHost {
  uId: string;
  workflowUId: string;
  workflowStepUId: string;
  createdOn: any;
  updatedOn: any;
  lastEditedByName: string;
  lastEditedById: string;
  name: string;
  workflowState: any;
  currentCascadeStepUId?: string;
  hostType: WorkflowHostType;
  sourceDistributionListUId?: string;
  sourceDatasetUId?: string;
  sourceDocumentUId?: string;
  sourceUserProfileUId?: string;
  recipientAddresses?: RecipientAddress[];
}

export interface MassNotificationRecipientState {
  configuredChannelUId: string;
  status: CommunicationRecipientStatus;
  displayStatusDetails?: string;
  displayStatusUId?: string;
}

export interface RecipientAddress {
  configuredChannelUId: string;
  address: string;
  isChannel: boolean;
}

export interface MassNotificationRecipientCountRequest {
  notificationUIds: string[];
}

export interface MassNotificationRecipientCount {
  massNotificationUId: string;
  count: number;
}

export interface MassNotificationRuleDetailsDto {
  massNotificationUId: string;
  ruleDatasetName?: string;
  ruleStepName?: string;
}

export interface MassNotificationRecipientStatsRequest {
  notificationUId: string;
  recipientUIds: string[];
  includeFirstResponses: boolean;
  includeAllResponses: boolean;
}

export interface MassNotificationRecipientStats {
  massNotificationRecipientUId: string;
  pushNotificationRequestId?: string;
  channelItems: MassNotificationRecipientStatItem[];
  responses: MassNotificationRecipientStatResponseItem[];
}

export interface MassNotificationRecipientStatItem {
  communicationRecipientUId?: string;
  configuredChannelUId?: string;
  channelUId?: string;
  status?: CommunicationRecipientStatus;
  statusDetails?: string;
  displayStatusDetails?: string;
  address?: string;
  openedTime?: string;
}

export interface MassNotificationRecipientStatResponseItem {
  responseText?: string;
  confirmationOptionsUId?: string;
  communicationRecipientUId?: string;
  configuredChannelUId?: string;
  responseChannelUId?: string;
  responseConfiguredChannelUId?: string;
  responseTime?: string;
  responseAddress?: string;
}

export interface MassNotificationStatsRequest extends MassNotification {}

export interface MassNotificationStats {
  notification: MassNotification;
  sendStartTime?: string;
  sendEndTime?: string;
  channelStats: MassNotificationChannelStats[];
  totalRecipientCount: number;
  deliveredRecipientCount: number;
  responsesStats: MassNotificationResponsesStats;
}

export interface MassNotificationResponsesStats {
  notification: MassNotification;
  responseCounts: MassNotificationResponseCount[];
  responses: MassNotificationResponseOverTime[];
  responseInterval: string;
}

export interface MassNotificationChannelStats {
  configuredChannelUId: string;
  channelUId: string;
  isDirectMessage: boolean;
  totalRecipientCount: number;
  statusCounts: MassNotificationStatusCount[];
  configuredChannel?: ConfiguredChannel;
}

export interface MassNotificationStatusCount {
  status?: CommunicationRecipientStatus;
  count: number;
}

export interface MassNotificationResponseCount {
  confirmationOptionsUId?: string;
  count: number;
}

export enum NotificationResponseStatus {
  None = 0,
  Responded = 1,
  RespondedPositive = 2,
  RespondedNegative = 3
}

export interface ClosestAssetRefRelatedDocumentsRequest {
  refFieldName?: string;
  refFilterGroup?: FilterGroup;
  queryMatchRule: DocumentQueryParams;
  locationGeoJson: Document;
  distanceGeoJson: string;
  withinMeters?: number;
  totalCountOnly: boolean;
  countsOnly: boolean;
}

export interface DocumentsRefRelatedDocumentsRequest {
  refFieldName?: string;
  refFilterGroup?: FilterGroup;
  documentUIds?: string[];
  queryMatchRule: DocumentQueryParams;
  totalCountOnly: boolean;
  countsOnly: boolean;
}

export interface RefRelatedDocumentsResponse {
  targetDatasetUId?: string;
  limitedCounts: boolean;
  totalCount: number;
  counts: { [uid: string]: number };
  documentUIds: string[];
}

export interface DocumentSelectionInfo {
  selectAll: boolean;
  documentUIds: string[]; // includes documents that are opposite to selectAll
}

export interface MassNotificationRecipientResponsesRequest {
  massNotificationUId: string;
  addressFilter?: string;
  channelFilter?: string;
  timeOrderUp?: boolean;
  workflowStepUIdsFilter?: string[];
  skip?: number;
  take?: number;
}

export interface MassNotificationRecipientResponsesResult {
  totalRecords: number;
  responses: MassNotificationRecipientResponseItem[];
}

export interface MassNotificationRecipientResponseItem {
  massNotificationRecipientUId?: string;
  massNotificationRecipientWorkflowStepUId?: string;
  communicationRecipientUId?: string;
  communicationUId?: string;
  channelUId?: string;
  name?: string;
  address?: string;
  responseText?: string;
  confirmationOptionsUId?: string;
  responseTime?: string;
}

export interface MassNotificationResponsesOverTimeRequest {
  notificationUId: string;
  startTime: any;
  endTime: any;
  intervalMinutes: number;
}

export interface MassNotificationResponseOverTime {
  sendCount: number;
  responseCount: number;
  time: string;
}

export interface MassNotificationRecipientsTransitionRequest {
  recipientUIds: string[];
  targetWorkflowStepUId: string;
}

export enum TemplateViewControlType {
  NotSet = 0,
  Image = 1,
  Header = 2,
  Text = 3,
  Divider = 4,
  Button = 5,
  Columns = 6,
  FormField = 7,
  Attachments = 8,
  Timeline = 9,
  Map = 10,
  SelectedAssets = 11,
  NotificationMessage = 12,
  ConfirmationMessage = 13,
  NotificationRecipientsStatus = 14,
  Summary = 15
}

export interface TemplateViewControlAction {
  actionType: ControlActionType;
  controlType: TemplateViewControlType;
  sourceElementId?: string;
}

export enum MassNotificationType {
  None = 0,
  Announcement = 1,
  SafetyCheck = 2,
  Survey = 3,
  Meeting = 4
}

export interface OrganizationChannel {
  channelUId: string;
  channelType: ChannelType;
  channelTypeDisplay: string;
  name: string;
  description: string;
  logoImagePath: string;
}

export interface ConfiguredChannel {
  uId: string;
  channelUId: string;
  name: string;
  description: string;
  channelName: string;
  channelDescription: string;
  channelType: ChannelType;
  channelTypeDisplay: string;
  logoImagePath: string;
  isActive: boolean;
  settings: any;
  secureSettings: any;
  createdOn: any;
  updatedOn: any;
  lastEditedByName: string;
  lastEditedById: string;
}

export interface DistributionList {
  uId: string;
  name: string;
  description: string;
  channelType: ChannelType;
  channelUId: string;
  channelTypeDisplay?: string;
  nameFieldUId: string;
  destinationFieldUId: string;
  isActive: boolean;
  createdOn: any;
  updatedOn: any;
  lastEditedByName: string;
  lastEditedById: string;
  queryMatchRule: QueryMatchRule;
  businessUnitUIds: string[];
  distributionListType: DistributionListType;
}

export enum DistributionListType {
  None = 0,
  FusionTeam = 1
}

export interface DistributionListsCountRequest {
  distributionListUIds: string[];
}

export interface DistributionListCount {
  distributionListUId: string;
  datasetUId: string;
  count: number;
}

export interface TeamGroupInfo {
  id: string;
  name: string;
  members: number;
}

export enum TeamsTestResult {
  None = 0,
  UserNotFound = 1,
  AppInstallationError = 2,
  AppWasNotApproved = 3,
  Error = 4,
  Success = 5
}

export interface QueryMatchRule {
  uId?: string | null;
  datasetUId?: string | null;
  root: FilterGroup | null;
}

export interface Template {
  uId?: string | null;
  name: string | null;
  description: string;
  channelType: ChannelType;
  channelUId?: string | null;
  channelTypeDisplay?: string;
  datasetUId: string;
  layout: string;
  isActive: boolean;
  createdOn: any;
  updatedOn: any;
  lastEditedByName: string;
  lastEditedById: string;
  settings: {
    toRecipients: any[];
    ccRecipients: any[];
    toReadOnly: boolean;
    ccReadOnly: boolean;
    bccRecipients: any[];
    subject: string;
    sendAsGroup: boolean;
    sendDocumentAttachments: boolean;
  };
}

export enum ChannelType {
  None = 0,
  Email = 1,
  Sms = 2,
  App = 3,
  MassNotifications = 4,
  Custom = 5,
  TextToSpeech = 6,
  WhatsApp = 7,
  MobilePush = 8
}

export const ChannelTypeIcons = {
  [ChannelType.Email]: 'icon-email',
  [ChannelType.Sms]: 'icon-satellite-dish',
  [ChannelType.App]: 'icon-small_comment',
  [ChannelType.MassNotifications]: 'icon-bullhorn',
  [ChannelType.Custom]: 'icon-email'
};

export const ChannelUIds = {
  TopoEmailChannelUId: '3f8b4b58-89ce-4d89-9b98-3ed09dfc77a7',
  SendGridEmailChannelUId: '6d101671-653f-481a-96cc-2e6bf672362c',
  SlackChannelUId: '5991b5a4-4ec4-4ca1-8ee1-b82350d70f33',
  EverbridgeChannelUId: '2c79fd53-42ad-4a07-84dd-27db322ffc96',
  MicrosoftTeamsChannelUId: 'f70a9cf7-6bac-497c-9c91-32d4bd5503ec',
  TwilioChannelUId: '03e3bda7-eb46-4cef-93d2-2fa0ac084324',
  TwilioTtsChannelUId: 'ba336b85-30ba-4751-a69a-1801d76f59f1',
  WhatsAppChannelUId: '87d7c379-0eba-42d8-81b4-ae60ed45af2c',
  HorizonSmsChannelUId: 'ffcca0a6-e8c7-4712-bec2-c752aaa7ca87',
  HorizonMobilePushChannelUId: '835b85a4-1f27-47df-b229-bb232cb465f4',
  GwtsMobileChannelUId: 'a887d7ab-f802-4622-a28d-8c18d4b7cbd0'
};

export enum TemplateControlType {
  None = 0,
  Image = 1,
  Text = 2,
  Divider = 3,
  Button = 4,
  Columns = 5,
  FormField = 6,
  Attachments = 7,
  Map = 8,
  SelectedAssetsList = 9,
  SectionHeader = 10,
  TableRow = 11,
  Table = 12,
  Timeline = 13
}

export interface WhatsAppTemplate {
  locale?: string;
  displayName?: string;
  textContent?: string;
  contentTemplateSID?: string;
  uId?: string;
  templateGroupUId?: string;
}

export interface TemplateControlAction {
  actionType: ControlActionType;
  controlType: TemplateControlType;
  sourceElementId?: string;
}

export enum ControlActionType {
  None = 0,
  Add = 1,
  Move = 2
}

export interface Communication {
  uId?: string;
  name?: string;
  description?: string;
  createdOn?: any;
  updatedOn?: any;
  lastEditedByName?: string;
  lastEditedById?: string;
  configuredChannelUId?: string;
  communicationTemplateUId?: string;
  datasetUId?: string;
  documentUId?: string;
  documentChangeUId?: string;
  customSubject?: string;
  customHtmlContent?: string;
  customTextContent?: string;
  customDocument?: Document;
  toDistributionListUIds?: string[];
  ccDistributionListUIds?: string[];
  bccDistributionListUIds?: string[];
  toCustomRecipients?: string[];
  ccCustomRecipients?: string[];
  bccCustomRecipients?: string[];
  toUserUIds?: string[];
  ccUserUIds?: string[];
  bccUserUIds?: string[];
  status?: CommunicationStatus;
  communicationStartTime?: any;
  communicationCompletionTime?: any;
  renderedTemplate?: RenderedTemplate;
  sendAsGroup?: boolean;
  settings?: any;
  mapUId?: string;
  toMassCommsRecipients?: MassCommsContact[];
  massNotificationUId?: string;
  renderedContent?: string;
  sendDocumentAttachments?: boolean;
  pathToMap?: string;
}

export interface RenderedTemplate {
  subject?: string;
  htmlContent?: string;
  textContent?: string;
}

export enum CommunicationStatus {
  None = 0,
  Pending = 1,
  Started = 2,
  Completed = 3,
  Cancelled = 4
}

export interface MassNotificationActivity {
  notificationUId?: string;
  documentUId?: string;
  recipientCount?: number;
  lastEditedById?: string;
  lastEditedByName?: string;
  updatedOn?: any;
  deleted?: boolean;
}

export interface CommunicationActivity {
  channelType?: ChannelType;
  recipientCount?: number;
  templateName?: string;
  lastEditedById?: string;
  lastEditedByName?: string;
  updatedOn?: any;
  communicationUId?: string;
  documentUId?: string;
  configuredName?: string;
}

export enum CommunicationRecipientStatus {
  None = 0,
  Pending = 1,
  Sent = 2,
  Bounced = 3,
  Error = 4,
  Delivered = 5,
  Opened = 6,
  Responded = 7
}

export enum RecipientType {
  NotSet = 0,
  To = 1,
  Cc = 2,
  Bcc = 3
}
export interface CommunicationRecipientActivity {
  uId: string;
  name?: string;
  recipient?: string;
  sentTime?: any;
  status: CommunicationRecipientStatus;
  recipientType: RecipientType;
  distributionListUId?: string;
  distributionListName?: string;
}

export interface EverbridgeTemplate {
  configuredChannelUId?: string;
  id: number | null;
  title?: string;
  textMessage?: string;
  textSmsMessage?: string;
}

export interface EverbridgeReplaceTokensRequest {
  templateDto: EverbridgeTemplate;
  documentDto: Document;
}

export interface MassCommsContact {
  contactId?: string;
  name?: string;
}

export interface MassCommsContactWithDistance {
  contactId?: string;
  name?: string;
  distance: number | null;
  distanceText: string | null;
}

export interface MassCommsContactDataset {
  contactsDatasetUId?: string;
  contactIdFieldUId?: string;
}
export interface DefaultConfiguredChannelSettings {
  phoneNumber?: string;
}
